import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import Select, { components } from "react-select";
import { saveAs } from "file-saver";
import "./KgTeoriciRaccolti.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import DatePickerModal from "../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../components/Lf23Loader";

const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);
    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return <div className="kg-teorici-select-more-badge">+{values.length - 2}</div>;
    }
    return null;
};

const KgTeoriciRaccolti = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    const today = new Date().toISOString().split("T")[0];
    const dataInizio = "2025-07-16";

    const [filterOptions, setFilterOptions] = useState({
        province: [],
        comuni: [],
        percorsi: [],
        cassonetti: []
    });

    const [filters, setFilters] = useState({
        provincia: [],
        comune: [],
        percorso: [],
        cassonetto: []
    });

    const [appliedFilters, setAppliedFilters] = useState({
        provincia: [],
        comune: [],
        percorso: [],
        cassonetto: []
    });

    // Date dedicate alla tabella
    const [tableDateFilters, setTableDateFilters] = useState({ dataDal: dataInizio, dataAl: today });
    const [appliedTableDateFilters, setAppliedTableDateFilters] = useState({ dataDal: dataInizio, dataAl: today });

    const [loading, setLoading] = useState(true);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [stats, setStats] = useState({
        kg_totali_stimati: 0,
        svuotamenti_totali: 0,
        media_kg_svuotamento: 0,
        campane_attive: 0
    });
    const [chartData, setChartData] = useState([]);
    const [aggregazione, setAggregazione] = useState("mese");
    const currentYear = new Date().getFullYear();
    const [selectedCompareYears, setSelectedCompareYears] = useState([
        { value: currentYear - 1, label: String(currentYear - 1) },
        { value: currentYear - 2, label: String(currentYear - 2) }
    ]);
    const [tableAggregazione, setTableAggregazione] = useState("mese");
    const [tableRaggruppamento, setTableRaggruppamento] = useState("dettaglio");
    const [showExtra, setShowExtra] = useState(false);
    const [tableData, setTableData] = useState({ rows: [], total: 0, page: 1, limit: 20, summary: {} });
    const [sortConfig, setSortConfig] = useState({ key: 'periodo', direction: 'desc' });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isFullscreenChart, setIsFullscreenChart] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                if (isFullscreen) setIsFullscreen(false);
                if (isFullscreenChart) setIsFullscreenChart(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen, isFullscreenChart]);

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
        setTablePage(1);
    };
    const [tablePage, setTablePage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [chartLoading, setChartLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);

    const isFirstRender = useRef(true);

    useEffect(() => {
        fetchFilterOptions();
    }, []);

    // Caricamento iniziale + cambio filtri → loader grande
    useEffect(() => {
        setLoading(true);
        setTablePage(1);
        fetchStats();
        fetchChartData();
        fetchTableData(1);
    }, [appliedFilters]);

    // Cambio aggregazione (skip primo render) → overlay solo grafico
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        fetchChartData();
    }, [aggregazione]);

    useEffect(() => {
        if (isFirstRender.current) return;
        fetchChartData();
    }, [selectedCompareYears]);

    // Cambio pagina tabella → overlay solo tabella
    useEffect(() => {
        if (tablePage > 1) {
            fetchTableData(tablePage);
        }
    }, [tablePage]);

    // Cambio aggregazione tabella → overlay solo tabella
    useEffect(() => {
        if (isFirstRender.current) return;
        setTablePage(1);
        fetchTableData(1);
    }, [tableAggregazione]);

    // Cambio raggruppamento tabella → overlay solo tabella
    useEffect(() => {
        if (isFirstRender.current) return;
        setTablePage(1);
        setSortConfig({
            key: 'periodo',
            direction: 'desc'
        });
    }, [tableRaggruppamento]);

    // Cambio ordinamento tabella → ri-fetch con nuovo sort
    useEffect(() => {
        if (isFirstRender.current) return;
        fetchTableData(1);
    }, [sortConfig]);

    // Toggle Extra → overlay solo tabella
    useEffect(() => {
        if (isFirstRender.current) return;
        setTablePage(1);
        fetchTableData(1);
    }, [showExtra]);

    // Cambio righe per pagina → overlay solo tabella
    useEffect(() => {
        if (isFirstRender.current) return;
        setTablePage(1);
        fetchTableData(1);
    }, [rowsPerPage]);

    // Cambio date tabella → overlay solo tabella
    useEffect(() => {
        if (isFirstRender.current) return;
        setTablePage(1);
        fetchTableData(1);
    }, [appliedTableDateFilters]);

    const fetchFilterOptions = async () => {
        try {
            const response = await fetch(`${API_URL}/interacta/kg-teorici-raccolti/filters`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const result = await response.json();
            setFilterOptions(result);
        } catch (error) {
            console.error("Errore nel caricamento dei filtri:", error);
        }
    };

    const fetchStats = async () => {
        try {
            const params = new URLSearchParams();
            if (appliedFilters.provincia.length) params.append("provincia", appliedFilters.provincia.map(o => o.value).join(","));
            if (appliedFilters.comune.length) params.append("comune", appliedFilters.comune.map(o => o.value).join(","));
            if (appliedFilters.percorso.length) params.append("percorso", appliedFilters.percorso.map(o => o.value).join(","));
            if (appliedFilters.cassonetto.length) params.append("cassonetto", appliedFilters.cassonetto.map(o => o.value).join(","));

            const response = await fetch(`${API_URL}/interacta/kg-teorici-raccolti?${params}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const result = await response.json();
            setStats(result);
        } catch (error) {
            console.error("Errore nel caricamento delle statistiche:", error);
        }
    };

    const fetchChartData = async () => {
        setChartLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("aggregazione", aggregazione);
            const anni = [currentYear, ...selectedCompareYears.map(o => o.value)];
            params.append("anni", anni.join(","));
            if (appliedFilters.provincia.length) params.append("provincia", appliedFilters.provincia.map(o => o.value).join(","));
            if (appliedFilters.comune.length) params.append("comune", appliedFilters.comune.map(o => o.value).join(","));
            if (appliedFilters.percorso.length) params.append("percorso", appliedFilters.percorso.map(o => o.value).join(","));
            if (appliedFilters.cassonetto.length) params.append("cassonetto", appliedFilters.cassonetto.map(o => o.value).join(","));

            const response = await fetch(`${API_URL}/interacta/kg-teorici-raccolti/chart?${params}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const result = await response.json();
            setChartData(result);
        } catch (error) {
            console.error("Errore nel caricamento dati grafico:", error);
        } finally {
            setChartLoading(false);
            setLoading(false);
        }
    };

    const fetchTableData = async (page = 1) => {
        setTableLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("aggregazione", tableAggregazione);
            params.append("raggruppamento", tableRaggruppamento);
            params.append("escludiExtra", !showExtra);
            params.append("page", page);
            params.append("limit", rowsPerPage);
            if (appliedTableDateFilters.dataDal) params.append("dataDal", appliedTableDateFilters.dataDal);
            if (appliedTableDateFilters.dataAl) params.append("dataAl", appliedTableDateFilters.dataAl);
            if (appliedFilters.provincia.length) params.append("provincia", appliedFilters.provincia.map(o => o.value).join(","));
            if (appliedFilters.comune.length) params.append("comune", appliedFilters.comune.map(o => o.value).join(","));
            if (appliedFilters.percorso.length) params.append("percorso", appliedFilters.percorso.map(o => o.value).join(","));
            if (appliedFilters.cassonetto.length) params.append("cassonetto", appliedFilters.cassonetto.map(o => o.value).join(","));
            if (sortConfig.key) {
                params.append("sortBy", sortConfig.key);
                params.append("sortDir", sortConfig.direction);
            }

            const response = await fetch(`${API_URL}/interacta/kg-teorici-raccolti/table?${params}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const result = await response.json();
            setTableData(result);
        } catch (error) {
            console.error("Errore nel caricamento tabella:", error);
        } finally {
            setTableLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        try {
            const params = new URLSearchParams();
            params.append("aggregazione", tableAggregazione);
            params.append("raggruppamento", tableRaggruppamento);
            params.append("escludiExtra", !showExtra);
            if (appliedTableDateFilters.dataDal) params.append("dataDal", appliedTableDateFilters.dataDal);
            if (appliedTableDateFilters.dataAl) params.append("dataAl", appliedTableDateFilters.dataAl);
            if (appliedFilters.provincia.length) params.append("provincia", appliedFilters.provincia.map(o => o.value).join(","));
            if (appliedFilters.comune.length) params.append("comune", appliedFilters.comune.map(o => o.value).join(","));
            if (appliedFilters.percorso.length) params.append("percorso", appliedFilters.percorso.map(o => o.value).join(","));
            if (appliedFilters.cassonetto.length) params.append("cassonetto", appliedFilters.cassonetto.map(o => o.value).join(","));
            if (sortConfig.key) {
                params.append("sortBy", sortConfig.key);
                params.append("sortDir", sortConfig.direction);
            }

            const response = await fetch(`${API_URL}/interacta/kg-teorici-raccolti/export?${params}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const blob = await response.blob();
            saveAs(blob, "kg_teorici_raccolti.xlsx");
        } catch (error) {
            console.error("Errore nell'export Excel:", error);
        }
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
    };

    const handleResetFilters = () => {
        const empty = {
            provincia: [],
            comune: [],
            percorso: [],
            cassonetto: []
        };
        setFilters(empty);
        setAppliedFilters(empty);

        setTableDateFilters({ dataDal: dataInizio, dataAl: today });
        setAppliedTableDateFilters({ dataDal: dataInizio, dataAl: today });
    };


    const MESI_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

    const formatPeriodo = (periodo, agg = aggregazione) => {
        if (!periodo) return "";
        const parti = periodo.split("-");
        if (agg === "gg" && parti.length === 3) {
            // "2026-03-02" → "02 Mar 26"
            return `${parti[2]} ${MESI_IT[parseInt(parti[1], 10) - 1]} ${parti[0].slice(2)}`;
        }
        if (agg === "sett" && parti.length === 2 && parti[1]?.startsWith("W")) {
            // "2026-W09" → "S09 '26"
            return `S${parti[1].replace("W", "")} ${parti[0]}`;
        }
        if (agg === "anno" || parti.length === 1) {
            return periodo;
        }
        if (parti.length === 2 && parti[1]) {
            // mese: "2026-03" → "Mar 26"
            const meseIdx = parseInt(parti[1], 10) - 1;
            if (meseIdx >= 0 && meseIdx < 12) {
                return `${MESI_IT[meseIdx]} ${parti[0].slice(2)}`;
            }
        }
        return periodo;
    };

    const isCurrentPeriod = (periodo) => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, "0");
        const d = String(now.getDate()).padStart(2, "0");
        if (aggregazione === "gg") return periodo === `${y}-${m}-${d}`;
        if (aggregazione === "mese") return periodo === `${y}-${m}`;
        if (aggregazione === "anno") return periodo === `${y}`;
        // sett: confronto settimana ISO corrente
        const oneJan = new Date(y, 0, 1);
        const days = Math.floor((now - oneJan) / 86400000);
        const weekNum = String(Math.ceil((days + oneJan.getDay() + 1) / 7)).padStart(2, "0");
        return periodo === `${y}-W${weekNum}`;
    };

    const AGGREGAZIONE_LABELS = { gg: "GG", sett: "Sett", mese: "Mese", anno: "Anno" };

    const getChartOption = () => {
        if (!chartData.periodi) return {};

        // Formattare le etichette dell'asse X
        const labels = chartData.periodi.map(p => {
            if (aggregazione === "mese") {
                return MESI_IT[parseInt(p, 10) - 1];
            }
            if (aggregazione === "sett") {
                return `S${p}`;
            }
            return p; // anno: "2024", "2025", "2026"
        });

        // Colori per anno: corrente = blu, -1 = celeste, -2 = grigio
        const coloriAnno = {
            [currentYear]: ["#1E40AF", "#3B81F5"],
            [currentYear - 1]: ["#15803D", "#22C55E"],
            [currentYear - 2]: ["#d0db0a", "#f5f10b"],
        };

        // Una serie bar per ogni anno presente nei dati
        const anniPresenti = Object.keys(chartData.data).map(Number).sort();
        let barSeries;

        if (aggregazione === "anno") {
            // caso anno: una serie per anno, ogni serie ha un solo dato al suo indice X
            barSeries = anniPresenti.map((anno, i) => {
                const datiAnno = chartData.data[anno];
                const colori = coloriAnno[anno] || ["#94A3B8", "#CBD5E1"];
                // Array con null ovunque tranne alla posizione di questo anno
                const data = anniPresenti.map((_, j) => j === i ? (datiAnno[0]?.kg_stimati || 0) : null);
                return {
                    name: `Kg ${anno}`,
                    type: "bar",
                    data,
                    barMaxWidth: 60,
                    barGap: "-100%",
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: colori[0] },
                            { offset: 1, color: colori[1] }
                        ]),
                        borderRadius: 5,
                    },
                    label: {
                        show: true,
                        position: "top",
                        formatter: (p) => p.value != null ? Number(p.value).toLocaleString("it-IT") : "",
                        fontSize: 11,
                        color: "#334155"
                    }
                };
            });
        } else {
            barSeries = anniPresenti.map(anno => {
                const datiAnno = chartData.data[anno];
                const colori = coloriAnno[anno] || ["#94A3B8", "#CBD5E1"];
                return {
                    name: `Kg ${anno}`,
                    type: "bar",
                    data: datiAnno.map(d => d.kg_stimati),
                    barMaxWidth: 40,
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: colori[0] },
                            { offset: 1, color: colori[1] }
                        ]),
                        borderRadius: 5,
                    },
                    label: {
                        show: anniPresenti.length === 1,
                        position: "top",
                        formatter: (p) => Number(p.value).toLocaleString("it-IT"),
                        fontSize: 11,
                        color: "#334155"
                    }
                };
            });
        }


        // Linea % solo per anno corrente
        const datiCorrente = chartData.data[currentYear] || [];
        const lineSeries = {
            name: `% Svuotamento Medio ${currentYear}`,
            type: "line",
            yAxisIndex: 1,
            data: datiCorrente.map(d => Number(d.perc_svuotamento_medio.toFixed(1))),
            itemStyle: { color: "#F59E0B" },
            lineStyle: { width: 3, color: "#F59E0B" },
            symbol: "circle",
            symbolSize: 8,
            label: {
                show: true,
                position: "top",
                formatter: (p) => `${Number(p.value).toFixed(1).replace(".", ",")}%`,
                fontSize: 11,
                color: "#F59E0B"
            }
        };

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "cross" },
                formatter: (params) => {
                    let html = `<strong>${params[0].axisValue}</strong><br/>`;
                    params.forEach(p => {
                        if (p.value == null) return;
                        if (p.seriesName.startsWith("Kg")) {
                            html += `${p.marker} ${p.seriesName}: <strong>${Number(p.value).toLocaleString("it-IT")}</strong><br/>`;
                        } else {
                            html += `${p.marker} ${p.seriesName}: <strong>${Number(p.value).toFixed(1).replace(".", ",")}%</strong><br/>`;
                        }
                    });
                    return html;
                }
            },
            legend: {
                data: aggregazione === "anno"
                    ? anniPresenti.map(anno => `Kg ${anno}`)
                    : [...barSeries.map(s => s.name), `% Svuotamento Medio ${currentYear}`],
                top: 0,
                right: 0
            },
            grid: { left: 60, right: 60, bottom: 40, top: 40 },
            xAxis: {
                type: "category",
                data: labels,
                axisLabel: { fontSize: 12 }
            },
            yAxis: [
                {
                    type: "value",
                    position: "left",
                    axisLabel: {
                        formatter: (val) => val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val
                    }
                },
                {
                    type: "value",
                    position: "right",
                    min: 0,
                    max: 100,
                    axisLabel: { formatter: "{value}%" }
                }
            ],
            series: aggregazione === "anno"
                ? [...barSeries]
                : [...barSeries, lineSeries]
        };
    };

    return (
        <div className="kg-teorici-container">
            {/* Header */}
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate("/produttivita")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Kg Teorici Raccolti</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfoModal(true)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">Analisi peso stimato raccolto per campana, percorso e territorio</p>
                    </div>
                </div>
            </header>

            {/* Modal Info */}
            {showInfoModal && (
                <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                    <path d="M10 8h6"></path>
                                    <path d="M10 12h6"></path>
                                    <path d="M10 16h4"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close" onClick={() => setShowInfoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa pagina analizza i <strong>kg teorici raccolti</strong> durante gli svuotamenti delle campane del vetro.
                                    Il peso stimato viene calcolato in base alla percentuale di riempimento rilevata al momento dello svuotamento.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Filtri disponibili</h4>
                                <ul>
                                    <li><strong>Data dal / Data al:</strong> Intervallo temporale di analisi</li>
                                    <li><strong>Cassonetto / Tag:</strong> Filtra per singola campana o gruppo di campane</li>
                                    <li><strong>Percorso:</strong> Filtra per percorso di raccolta</li>
                                    <li><strong>Comune:</strong> Filtra per uno o piu' comuni</li>
                                    <li><strong>Provincia:</strong> Filtra per provincia</li>
                                </ul>
                            </div>
                            <div className="modal-section">
                                <h4>Grafico</h4>
                                <p>
                                    Il grafico mostra l'andamento nel tempo dei kg stimati (barre) e della percentuale media di svuotamento (linea).
                                    E' possibile cambiare l'aggregazione temporale tra settimana, mese e anno.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Tabella dettaglio</h4>
                                <ul>
                                    <li><strong>Aggregazione:</strong> Raggruppa i dati per giorno, settimana, mese o anno</li>
                                    <li><strong>Raggruppamento:</strong> Visualizza il dettaglio per singola campana, oppure aggregato per comune o provincia</li>
                                    <li><strong>EXTRA ON/OFF:</strong> Mostra o nasconde i comuni con dicitura "EXTRA" nel nome</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area">
                {/* Toggle Filtri */}
                <div className="kg-teorici-top-controls">
                    <button
                        className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                    </button>
                </div>

                {/* Barra Filtri */}
                {showFilters && (
                    <div className="kg-teorici-filters">
                        <div className="kg-teorici-filters-grid">
                            <div className="kg-teorici-filter-item">
                                <label>Cassonetto / Tag</label>
                                <Select
                                    isMulti
                                    value={filters.cassonetto}
                                    onChange={(sel) => setFilters({ ...filters, cassonetto: sel || [] })}
                                    options={filterOptions.cassonetti.map(c => ({ value: c, label: c }))}
                                    placeholder={`Tutti`}
                                    classNamePrefix="kg-teorici-select"
                                    components={{ MultiValue: MultiValueContainer }}
                                    isClearable
                                />
                            </div>
                            <div className="kg-teorici-filter-item">
                                <label>Percorso</label>
                                <Select
                                    isMulti
                                    value={filters.percorso}
                                    onChange={(sel) => setFilters({ ...filters, percorso: sel || [] })}
                                    options={filterOptions.percorsi.map(p => ({ value: p, label: p }))}
                                    placeholder={`Tutti`}
                                    classNamePrefix="kg-teorici-select"
                                    components={{ MultiValue: MultiValueContainer }}
                                    isClearable
                                />
                            </div>
                            <div className="kg-teorici-filter-item">
                                <label>Comune</label>
                                <Select
                                    isMulti
                                    value={filters.comune}
                                    onChange={(sel) => setFilters({ ...filters, comune: sel || [] })}
                                    options={filterOptions.comuni.map(c => ({ value: c, label: c }))}
                                    placeholder={`Tutti `}
                                    classNamePrefix="kg-teorici-select"
                                    components={{ MultiValue: MultiValueContainer }}
                                    isClearable
                                />
                            </div>
                            <div className="kg-teorici-filter-item">
                                <label>Provincia</label>
                                <Select
                                    isMulti
                                    value={filters.provincia}
                                    onChange={(sel) => setFilters({ ...filters, provincia: sel || [] })}
                                    options={filterOptions.province.map(p => ({ value: p, label: p }))}
                                    placeholder={`Tutte`}
                                    classNamePrefix="kg-teorici-select"
                                    components={{ MultiValue: MultiValueContainer }}
                                    isClearable
                                />
                            </div>
                        </div>
                        <div className="kg-teorici-filters-actions">
                            <button className="kg-teorici-btn-apply" onClick={handleApplyFilters}>
                                Applica
                            </button>
                            <button className="kg-teorici-btn-reset" onClick={handleResetFilters}>
                                Reset
                            </button>
                        </div>
                    </div>
                )}

                {/* KPI Stats */}
                <div className="kg-teorici-stats-grid">
                    <div className="kg-teorici-stat-card">
                        <div className="kg-teorici-stat-icon blue">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                                <path d="M2 17l10 5 10-5"></path>
                                <path d="M2 12l10 5 10-5"></path>
                            </svg>
                        </div>
                        <div className="kg-teorici-stat-content">
                            <p className="kg-teorici-stat-value">{Number(stats.kg_totali_stimati).toLocaleString("it-IT")}</p>
                            <p className="kg-teorici-stat-label">Kg Totali Stimati</p>
                        </div>
                    </div>
                    <div className="kg-teorici-stat-card">
                        <div className="kg-teorici-stat-icon green">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                        </div>
                        <div className="kg-teorici-stat-content">
                            <p className="kg-teorici-stat-value">{Number(stats.svuotamenti_totali).toLocaleString("it-IT")}</p>
                            <p className="kg-teorici-stat-label">Svuotamenti Totali</p>
                        </div>
                    </div>
                    <div className="kg-teorici-stat-card">
                        <div className="kg-teorici-stat-icon amber">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 3h15v13H1z"></path>
                                <path d="M16 8h4l3 3v5h-7V8z"></path>
                                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                <circle cx="18.5" cy="18.5" r="2.5"></circle>
                            </svg>
                        </div>
                        <div className="kg-teorici-stat-content">
                            <p className="kg-teorici-stat-value">{Number(stats.media_kg_svuotamento).toFixed(1).replace(".", ",")}</p>
                            <p className="kg-teorici-stat-label">Media Kg / Svuotamento</p>
                        </div>
                    </div>
                    <div className="kg-teorici-stat-card">
                        <div className="kg-teorici-stat-icon purple">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                        </div>
                        <div className="kg-teorici-stat-content">
                            <p className="kg-teorici-stat-value">{Number(stats.campane_attive).toLocaleString("it-IT")}</p>
                            <p className="kg-teorici-stat-label">Campane Attive</p>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <Lf23Loader />
                ) : (
                    <>
                        {/* Grafico Andamento */}
                        <div className={`kg-teorici-chart-card${isFullscreenChart ? ' kg-teorici-fullscreen' : ''}`} style={{ position: "relative" }}>
                            <RotateDevice active={isFullscreenChart} />
                            {chartLoading && (
                                <div className="kg-teorici-table-overlay">
                                    <Lf23Loader testo="Caricamento dati..." />
                                </div>
                            )}
                            <div className="kg-teorici-chart-header">
                                <h3 className="kg-teorici-chart-title">
                                    Andamento Kg Teorici Raccolti
                                </h3>
                                <div className="kg-teorici-chart-controls">
                                    <div className="kg-teorici-confronto-anni">
                                        <span className="kg-teorici-aggregazione-label">CONFRONTA CON</span>
                                        <Select
                                            isMulti
                                            value={selectedCompareYears}
                                            onChange={(sel) => setSelectedCompareYears(sel || [])}
                                            options={[
                                                { value: currentYear - 1, label: String(currentYear - 1) },
                                                { value: currentYear - 2, label: String(currentYear - 2) }
                                            ]}
                                            placeholder="Nessun anno"
                                            classNamePrefix="kg-teorici-select"
                                            isClearable
                                        />
                                    </div>
                                    <div className="kg-teorici-aggregazione">
                                        <span className="kg-teorici-aggregazione-label">AGGREGAZIONE</span>
                                        <div className="kg-teorici-aggregazione-toggle">
                                            {["sett", "mese", "anno"].map((val) => (
                                                <button
                                                    key={val}
                                                    className={`kg-teorici-agg-btn ${aggregazione === val ? "active" : ""}`}
                                                    onClick={() => setAggregazione(val)}
                                                >
                                                    {AGGREGAZIONE_LABELS[val]}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <button
                                        className="kg-teorici-fullscreen-btn"
                                        onClick={() => setIsFullscreenChart(!isFullscreenChart)}
                                        title={isFullscreenChart ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            {isFullscreenChart ? (
                                                <>
                                                    <polyline points="4 14 10 14 10 20"></polyline>
                                                    <polyline points="20 10 14 10 14 4"></polyline>
                                                    <line x1="14" y1="10" x2="21" y2="3"></line>
                                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                                </>
                                            ) : (
                                                <>
                                                    <polyline points="15 3 21 3 21 9"></polyline>
                                                    <polyline points="9 21 3 21 3 15"></polyline>
                                                    <line x1="21" y1="3" x2="14" y2="10"></line>
                                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                                </>
                                            )}
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <ReactECharts
                                option={getChartOption()}
                                notMerge={true}
                                style={{ height: 400 }}
                                opts={{ renderer: "canvas" }}
                            />
                        </div>

                        {/* Tabella Dettaglio */}
                        <div className={`kg-teorici-table-card${isFullscreen ? ' kg-teorici-fullscreen' : ''}`} style={{ position: "relative" }}>
                            <RotateDevice active={isFullscreen} />
                            <div className="kg-teorici-table-toggles">
                                <div className="kg-teorici-table-date-item">
                                    <label>Data dal</label>
                                    <DatePickerModal
                                        value={tableDateFilters.dataDal}
                                        onChange={(e) => setTableDateFilters({ ...tableDateFilters, dataDal: e.target.value })}
                                        label="Data dal"
                                    />
                                </div>
                                <div className="kg-teorici-table-date-item">
                                    <label>Data al</label>
                                    <DatePickerModal
                                        value={tableDateFilters.dataAl}
                                        onChange={(e) => setTableDateFilters({ ...tableDateFilters, dataAl: e.target.value })}
                                        label="Data al"
                                    />
                                </div>
                                <button
                                    className="kg-teorici-btn-apply"
                                    onClick={() => setAppliedTableDateFilters({ ...tableDateFilters })}
                                >
                                    Applica
                                </button>
                                <div className="kg-teorici-aggregazione">
                                    <span className="kg-teorici-aggregazione-label">AGGREGAZIONE</span>
                                    <div className="kg-teorici-aggregazione-toggle">
                                        {["gg", "sett", "mese", "anno"].map((val) => (
                                            <button
                                                key={val}
                                                className={`kg-teorici-agg-btn ${tableAggregazione === val ? "active" : ""}`}
                                                onClick={() => setTableAggregazione(val)}
                                            >
                                                {AGGREGAZIONE_LABELS[val]}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="kg-teorici-aggregazione">
                                    <span className="kg-teorici-aggregazione-label">RAGGRUPPAMENTO</span>
                                    <div className="kg-teorici-aggregazione-toggle">
                                        {["dettaglio", "comune", "provincia"].map((val) => (
                                            <button
                                                key={val}
                                                className={`kg-teorici-agg-btn ${tableRaggruppamento === val ? "active" : ""}`}
                                                onClick={() => setTableRaggruppamento(val)}
                                            >
                                                {val.charAt(0).toUpperCase() + val.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    className={`kg-teorici-extra-btn ${showExtra ? "active" : ""}`}
                                    onClick={() => setShowExtra(!showExtra)}
                                    title={showExtra ? "Nascondi comuni EXTRA" : "Mostra comuni EXTRA"}
                                >
                                    EXTRA {showExtra ? "ON" : "OFF"}
                                </button>
                                <button
                                    className="kg-teorici-export-btn"
                                    onClick={handleDownloadExcel}
                                    title="Esporta i dati filtrati in formato Excel"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta Excel
                                </button>
                                <button
                                    className="kg-teorici-fullscreen-btn"
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreen ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            {tableLoading && (
                                <div className="kg-teorici-table-overlay">
                                    <Lf23Loader testo="Caricamento dati..." />
                                </div>
                            )}
                            <div className="kg-teorici-table-wrapper">
                                <table className="kg-teorici-table">
                                    <thead>
                                        <tr>
                                            {[
                                                ...(tableRaggruppamento === "dettaglio" ? [
                                                    { label: 'Periodo', key: 'periodo' },
                                                    { label: 'Codice Campana', key: 'codice_campana' },
                                                    { label: 'Nome Campana', key: 'nome_campana' },
                                                    { label: 'Percorso', key: 'nome_percorso' },
                                                    { label: 'Comune', key: 'comune' },
                                                    { label: 'Provincia', key: 'provincia' },
                                                ] : []),
                                                ...(tableRaggruppamento === "comune" ? [
                                                    { label: 'Periodo', key: 'periodo' },
                                                    { label: 'Comune', key: 'comune' }
                                                ] : []),
                                                ...(tableRaggruppamento === "provincia" ? [
                                                    { label: 'Periodo', key: 'periodo' },
                                                    { label: 'Provincia', key: 'provincia' }
                                                ] : []),
                                                { label: 'Svuotam.', key: 'svuotamenti' },
                                                { label: '% Svuot. Media', key: 'perc_svuotamento_media' },
                                                { label: 'Kg Stimati', key: 'kg_stimati' },
                                            ].map(({ label, key }) => (
                                                <th key={key} className="kg-teorici-th-sortable" onClick={() => handleSort(key)}>
                                                    {label}
                                                    <span className="kg-teorici-sort-icon">
                                                        {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.rows.length === 0 ? (
                                            <tr>
                                                <td colSpan={tableRaggruppamento === "dettaglio" ? 9 : 5} className="kg-teorici-table-empty">Nessun dato disponibile</td>
                                            </tr>
                                        ) : (
                                            tableData.rows.map((row, i) => (
                                                <tr key={i}>
                                                    {tableRaggruppamento === "dettaglio" && (
                                                        <>
                                                            <td>{formatPeriodo(row.periodo, tableAggregazione)}</td>
                                                            <td><span className="kg-teorici-badge">{row.codice_campana}</span></td>
                                                            <td>{row.nome_campana}</td>
                                                            <td>{row.nome_percorso}</td>
                                                            <td>{row.comune}</td>
                                                            <td>{row.provincia}</td>
                                                        </>
                                                    )}
                                                    {tableRaggruppamento === "comune" && (
                                                        <>
                                                            <td>{formatPeriodo(row.periodo, tableAggregazione)}</td>
                                                            <td>{row.comune}</td>
                                                        </>
                                                    )}
                                                    {tableRaggruppamento === "provincia" && (
                                                        <>
                                                            <td>{formatPeriodo(row.periodo, tableAggregazione)}</td>
                                                            <td>{row.provincia}</td>
                                                        </>
                                                    )}
                                                    <td>{row.svuotamenti}</td>
                                                    <td>{Number(row.perc_svuotamento_media).toFixed(0)}%</td>
                                                    <td className="kg-teorici-table-kg">{Number(row.kg_stimati).toLocaleString("it-IT")}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Paginazione */}
                            <div className="kg-teorici-pagination">
                                <button
                                    className="kg-teorici-page-btn"
                                    onClick={() => setTablePage(p => Math.max(1, p - 1))}
                                    disabled={tablePage <= 1}
                                >
                                    &larr; Precedente
                                </button>
                                <div className="kg-teorici-page-info">
                                    <span>Pagina <strong>{tablePage}</strong> di <strong>{Math.ceil(tableData.total / tableData.limit) || 1}</strong></span>
                                    {tableData.summary && (
                                        <span className="kg-teorici-page-summary">
                                            Totale: {Number(tableData.summary.campane_totali || 0).toLocaleString("it-IT")} campane &middot; {Number(tableData.summary.svuotamenti_totali || 0).toLocaleString("it-IT")} svuotamenti &middot; {Number(tableData.summary.kg_totali || 0).toLocaleString("it-IT")} kg
                                        </span>
                                    )}
                                </div>
                                <button
                                    className="kg-teorici-page-btn"
                                    onClick={() => setTablePage(p => p + 1)}
                                    disabled={tablePage >= Math.ceil(tableData.total / tableData.limit)}
                                >
                                    Successiva &rarr;
                                </button>

                                <div className="items-per-page-kg">
                                    <span className="items-label-kg">Elementi per pagina:</span>
                                    <div className="items-buttons-kg">
                                        {[10, 20, 50, 100].map((num) => (
                                            <button
                                                key={num}
                                                className={`items-btn-kg ${rowsPerPage === num ? "active" : ""}`}
                                                onClick={() => {
                                                    setRowsPerPage(num);
                                                    setTablePage(1);
                                                }}
                                            >
                                                {num}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default KgTeoriciRaccolti;




